import { performanceApi } from '@/entities/performance/api/performance.api';
import { PrescriptionLineCard } from '@/entities/performance/components/PrescriptionLineCard';
import {
  useAllPerformanceClinics,
  useClinicImages,
  useImageDrugs,
} from '@/entities/performance/hooks/usePerformanceQueries';
import { useRefreshableImageUrl } from '@/entities/performance/hooks/useRefreshableImageUrl';
import { monthOf, ymLabel } from '@/entities/performance/lib/performance';
import { isNetworkError } from '@/shared/api/http-client';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import { Breadcrumbs } from '@/shared/components/ui/breadcrumbs';
import { Button } from '@/shared/components/ui/button';
import { Icon } from '@/shared/components/ui/icon';
import { PageControl } from '@/shared/components/ui/page-control';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { toApiYm } from '@/shared/utils/format';
import { toastApi } from '@/shared/utils/toast';
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ImageStatusBadge } from './components/ImagesTab';
import { dashboardLink, downloadFile } from './lib';
import type { PrescriptionImage } from '@/entities/performance/api/performance.api';

const ZOOM_MIN = 25;
const ZOOM_MAX = 400;
const ZOOM_STEP = 25;
/** 썸네일 줄에 한 번에 보이는 장수 (Figma 789:8236) */
const THUMB_WINDOW = 6;

const IMAGE_PATH =
  `${ROUTE_PATHS.PERFORMANCE_MONTH}/images/$imageCode` as const;

const Thumb = ({
  ym,
  clinicId,
  image,
  selected,
  onClick,
}: {
  ym: string;
  clinicId: number;
  image: PrescriptionImage;
  selected: boolean;
  onClick: () => void;
}) => {
  const { src, onError } = useRefreshableImageUrl(ym, clinicId, image);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${image.displayName} 보기`}
      aria-current={selected || undefined}
      className={cn(
        'h-[60px] w-20 shrink-0 overflow-hidden rounded-12 bg-surface-grey outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]',
        selected && 'border-3 border-brand-500',
      )}
    >
      {src && (
        <img
          src={src}
          onError={onError}
          alt=""
          className="size-full object-cover object-top"
        />
      )}
    </button>
  );
};

/**
 * M-18 처방 이미지 상세 — 모달에서 별도 페이지로 바뀌었다 (Figma 742:7224)
 * 주소: /performance/YYYY-MM/images/{imageCode}?clinicId= — 이미지를 넘기면 주소가 바뀌고(replace),
 * 뒤로가기 · Esc · Breadcrumbs로 M-16 처방 이미지 탭으로 돌아간다.
 * - 확대 25~400% (25 단위), 맞춤 = 100% · 회전 0°, 회전은 90°씩. ←/→ 키로 이미지 이동
 * - [원본 내려받기]: download-url로 새 주소를 받아 저장한다 (기획 추가 6)
 * - 오른쪽: 이 이미지에 연결된 처방 실적 (선택 정산월분)
 */
export const PerformanceImageViewerPage = () => {
  const navigate = useNavigate();
  const { yearMonth = '', imageCode = '' } = useParams({
    strict: false,
  }) as { yearMonth?: string; imageCode?: string };
  const { clinicId } = useSearch({ strict: false }) as { clinicId?: number };
  const ym = toApiYm(yearMonth);

  const images = useClinicImages(ym, clinicId);
  const clinics = useAllPerformanceClinics(ym);
  const clinicName =
    clinics.data?.content.find((c) => c.clinicId === clinicId)?.clinicName ??
    '';
  const list = images.data ?? [];
  const index = Math.max(
    0,
    list.findIndex((i) => i.imageCode === decodeURIComponent(imageCode)),
  );
  const image = list[index] as PrescriptionImage | undefined;
  const lines = useImageDrugs(ym, clinicId ?? 0, image?.imageCode);
  const { src, onError } = useRefreshableImageUrl(ym, clinicId ?? 0, image);

  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [downloading, setDownloading] = useState(false);
  // 목록 아래쪽 카드에서 들어와도 페이지 맨 위부터 보이게 한다
  useEffect(() => window.scrollTo(0, 0), []);
  useEffect(() => {
    setZoom(100);
    setRotation(0);
  }, [image?.imageCode]);

  const monthPage = {
    to: ROUTE_PATHS.PERFORMANCE_MONTH,
    params: { yearMonth },
    search: { clinicId, tab: 'images' as const },
  };
  const goImage = (i: number) => {
    const target = list[i];
    if (!target) return;
    void navigate({
      to: IMAGE_PATH,
      params: { yearMonth, imageCode: target.imageCode },
      search: { clinicId },
      replace: true,
    });
  };
  const close = () => {
    if (window.history.length > 1) window.history.back();
    else void navigate(monthPage);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // 입력 · 드로어 · 팝업 위 키는 건드리지 않는다
      if (e.defaultPrevented || document.querySelector('[role="dialog"]'))
        return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft' && index > 0) goImage(index - 1);
      if (e.key === 'ArrowRight' && index < list.length - 1) goImage(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const downloadOriginal = async () => {
    if (!image || clinicId === undefined) return;
    setDownloading(true);
    try {
      const { imageUrl } = await performanceApi.imageDownloadUrl(
        ym,
        clinicId,
        image.imageCode,
      );
      downloadFile(imageUrl, image.displayName);
    } catch (error) {
      if (!isNetworkError(error))
        toastApi.error('원본을 내려받지 못했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setDownloading(false);
    }
  };

  const windowStart = Math.min(
    Math.max(0, index - Math.floor(THUMB_WINDOW / 2)),
    Math.max(0, list.length - THUMB_WINDOW),
  );
  const visibleThumbs = list.slice(windowStart, windowStart + THUMB_WINDOW);
  const items = lines.data?.content ?? [];

  if (!/^\d{6}$/.test(ym) || clinicId === undefined)
    return <EmptyState message="잘못된 주소예요." />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <Breadcrumbs
          size="medium"
          items={[
            { label: '실적', href: 'dashboard' },
            { label: ymLabel(ym), href: 'month' },
            { label: clinicName || '병의원', href: 'clinic' },
            { label: '처방 이미지' },
          ]}
          renderLink={(item, children) =>
            item.href === 'dashboard' ? (
              <Link {...dashboardLink(ym)}>{children}</Link>
            ) : item.href === 'month' ? (
              <Link to={ROUTE_PATHS.PERFORMANCE_MONTH} params={{ yearMonth }}>
                {children}
              </Link>
            ) : (
              <Link {...monthPage}>{children}</Link>
            )
          }
        />
        <h1 className="text-heading-24-b text-fg-primary">
          {monthOf(ym)}월 처방 이미지
        </h1>
      </div>

      {images.isPending ? (
        <LoadingBlock className="h-[810px]" />
      ) : images.isError ? (
        <ErrorState onRetry={() => void images.refetch()} />
      ) : !image ? (
        <EmptyState message="처방 이미지를 찾을 수 없어요." />
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[680px_minmax(0,1fr)]">
          {/* 왼쪽 — 이미지 · 확대 도구 · 썸네일 */}
          <section className="flex flex-col items-center gap-6 rounded-20 bg-surface-white py-6">
            <div className="flex w-full flex-col items-center gap-4 px-6">
              <div className="flex w-full flex-col">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-subtitle-18-b text-fg-primary">
                      {image.displayName}
                    </h2>
                    <ImageStatusBadge image={image} />
                  </div>
                  <Button
                    variant="grey"
                    leftIcon={<Icon name="download-line-24" />}
                    loading={downloading}
                    disabled={!image.imageUrl}
                    onClick={() => void downloadOriginal()}
                    className="shrink-0"
                  >
                    원본 내려받기
                  </Button>
                </div>
                <p className="text-body-13-r text-fg-tertiary">
                  {[clinicName, `${ym.slice(0, 4)}년 ${ym.slice(4, 6)}월`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>

              <div className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden border border-line-grey bg-surface-grey">
                {src ? (
                  <img
                    src={src}
                    onError={onError}
                    alt={`처방 이미지 ${image.displayName}`}
                    draggable={false}
                    className="max-h-full max-w-full object-contain transition-transform duration-150 select-none"
                    style={{
                      transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                    }}
                  />
                ) : (
                  <p className="text-body-14-r text-fg-tertiary">
                    원본 이미지를 불러올 수 없어요.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 rounded-16 bg-surface-grey p-1">
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="small"
                    aria-label="축소"
                    disabled={zoom <= ZOOM_MIN}
                    onClick={() =>
                      setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))
                    }
                    leftIcon={<Icon name="minus-line-16" />}
                  />
                  <span className="w-[52px] px-1 text-center text-button-16-sb text-fg-tertiary">
                    {zoom}%
                  </span>
                  <Button
                    variant="outline"
                    size="small"
                    aria-label="확대"
                    disabled={zoom >= ZOOM_MAX}
                    onClick={() =>
                      setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))
                    }
                    leftIcon={<Icon name="plus-line-16" />}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => {
                      setZoom(100);
                      setRotation(0);
                    }}
                  >
                    맞춤
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                  >
                    회전
                  </Button>
                </div>
              </div>
            </div>

            <div aria-hidden className="h-px w-full bg-line-grey" />

            <div className="flex w-full items-center justify-between gap-2 px-6">
              <Button
                variant="grey"
                size="small"
                aria-label="이전 이미지"
                disabled={index === 0}
                onClick={() => goImage(index - 1)}
                leftIcon={<Icon name="nav-arrow-left-line-16" />}
              />
              <div className="flex min-w-0 flex-1 justify-center gap-3 overflow-hidden">
                {visibleThumbs.map((img, i) => (
                  <Thumb
                    key={img.imageCode}
                    ym={ym}
                    clinicId={clinicId}
                    image={img}
                    selected={windowStart + i === index}
                    onClick={() => goImage(windowStart + i)}
                  />
                ))}
              </div>
              <Button
                variant="grey"
                size="small"
                aria-label="다음 이미지"
                disabled={index >= list.length - 1}
                onClick={() => goImage(index + 1)}
                leftIcon={<Icon name="nav-arrow-right-line-16" />}
              />
            </div>
            <PageControl current={index + 1} total={list.length} />
          </section>

          {/* 오른쪽 — 연결된 처방 실적 */}
          <section className="flex flex-col gap-4 rounded-20 bg-surface-white p-6">
            <p className="flex items-center gap-2 text-subtitle-18-b">
              <span className="text-fg-primary">연결된 처방 실적</span>
              {lines.data && (
                <span className="text-fg-accent">
                  {lines.data.totalCount}품목
                </span>
              )}
            </p>
            {lines.isPending ? (
              <LoadingBlock className="h-[186px]" />
            ) : lines.isError ? (
              <ErrorState onRetry={() => void lines.refetch()} />
            ) : items.length === 0 ? (
              <EmptyState
                className="py-10"
                message="아직 이 이미지에서 확인된 처방 내역이 없어요."
              />
            ) : (
              <div className="flex flex-col gap-4">
                {items.map((line) => (
                  <PrescriptionLineCard
                    key={line.prescriptionId}
                    line={line}
                    clinicName={clinicName}
                    variant="stacked"
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};
